import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import {
  STATS_YEAR,
  CURRENT_YEAR,
  champSeriesDateRanges
} from '../../Config/League/league.config'
import { isBetweenDates } from '../../Utils/logicHelper'

export type SeasonType = 'regular' | 'post' | 'allstar' | 'champseries'
export type Season = { seasonType: SeasonType; year: number }
type SeasonContextType = {
  seasonSelect: Season
  playerComparisonSeasonSelect: Season
  onSeasonDropdownChange: (seasonOpts: Season) => void
  onPlayerCompareSeasonChange: (seasonOpts: Season) => void
}

export const SeasonContext = createContext<SeasonContextType | null>(null)

export const SeasonProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const csRange = champSeriesDateRanges[CURRENT_YEAR]
  const [seasonSelect, setSeasonSelect] = useState<Season>({
    seasonType: isBetweenDates(csRange.startTime, csRange.endTime)
      ? 'champseries'
      : 'regular',
    year: isBetweenDates(csRange.startTime, csRange.endTime) ? CURRENT_YEAR : STATS_YEAR,
  })
  const [playerComparisonSeasonSelect, setPlayerComparisonSeasonSelect] =
    useState<Season>({
      seasonType: isBetweenDates(csRange.startTime, csRange.endTime)
        ? 'champseries'
        : 'regular',
      year: isBetweenDates(csRange.startTime, csRange.endTime) ? CURRENT_YEAR : STATS_YEAR,
    })

  const onSeasonDropdownChange = useCallback((seasonOpts: Season): void => {
    setSeasonSelect(seasonOpts)
  }, [])

  const onPlayerCompareSeasonChange = useCallback((seasonOpts: Season): void => {
    setPlayerComparisonSeasonSelect({
      seasonType: seasonOpts.seasonType,
      year: seasonOpts.year,
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      seasonSelect,
      playerComparisonSeasonSelect,
      onSeasonDropdownChange,
      onPlayerCompareSeasonChange,
    }),
    [seasonSelect, playerComparisonSeasonSelect, onSeasonDropdownChange, onPlayerCompareSeasonChange]
  )

  return (
    <SeasonContext.Provider value={contextValue}>
      {children}
    </SeasonContext.Provider>
  )
}

export const useSeasonContext = (): SeasonContextType => {
  const context = useContext(SeasonContext)
  if (!context) {
    throw new Error('useSeasonContext must be used within a SeasonProvider')
  }
  return context
}
